import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
// Wave 5 Phase A (A3 / R12): Inter loaded from `@fontsource-variable/inter`
// so the build owns the font, replacing the Google Fonts CDN <link> in
// `index.html`. This keeps the font available offline and removes a
// runtime third-party dependency.
import '@fontsource-variable/inter';
import './index.css';
import App from './App.tsx';
import { loadRuntimeConfig } from './state/runtime-config';
import { useTenantStore } from './state/tenant.store';
import { useModeStore } from './state/mode.store';
import { initializeTelemetry } from './services/telemetry';
import { setLearningLoopRuntimeConfig } from './state/learning-loop.store';

/**
 * Boot sequence:
 *   1. Fetch /config.json (templated by docker-entrypoint.sh from K8s env vars)
 *   2. Hydrate tenant + mode stores with the validated config
 *   3. Activate the App Insights sink if the connection string is present
 *   4. Render <App />
 *
 * On any failure loadRuntimeConfig() resolves to fallback defaults and logs
 * a warning - boot must not block per ADR-PROTO-001 production-grade
 * resilience guidance. The render call always fires.
 */
async function bootstrap(): Promise<void> {
  try {
    const cfg = await loadRuntimeConfig();
    useTenantStore.getState().hydrateFromConfig(cfg);
    useModeStore.getState().setMode(cfg.mode);
    // PROD-573 + PROD-593: activate App Insights sink if the connection
    // string is wired. No-op when null (dev / Demo / preview).
    initializeTelemetry(cfg.appInsightsConnectionString);
    // PROD-592 + PROD-593: forward the App Insights connection string +
    // workspace id to the learning-loop store so the default factory
    // can build a Live MetricsClient with real credentials.
    setLearningLoopRuntimeConfig({
      appInsightsConnectionString: cfg.appInsightsConnectionString,
      appInsightsWorkspaceId: cfg.appInsightsWorkspaceId,
    });
  } catch (err) {
    console.warn('[bootstrap] runtime config hydration failed; rendering with sync defaults', err);
  }

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

void bootstrap();
